import type { ReactNode } from 'react';

import { ListingSkeleton } from '@eliadmin/core/src/components/nt/category/listing/listing-paging';
import { Breadcrumbs, type BreadcrumbItem } from '@eliadmin/core/src/components/nt/ui/breadcrumbs';

/** Grid of the tiles: 4 columns at 1280 and up (314 px at 1728), 3 from 1024, 2 from 640, 1 below. */
export const SEARCH_GRID =
  'grid grid-cols-1 gap-y-8 sm:grid-cols-2 sm:gap-x-4 md:gap-x-6 lg:grid-cols-3 xl:grid-cols-4';

/** `sizes` of the square photo in that grid (container = viewport − gutters, gap 24). */
export const TILE_SIZES =
  '(min-width: 1392px) 314px, (min-width: 1280px) calc((100vw - 136px) / 4), (min-width: 1024px) calc((100vw - 112px) / 3), (min-width: 640px) calc((100vw - 72px) / 2), 100vw';

/** The placeholder of the page while the query, the customer or the products are loading. */
export function ProductsSkeleton({ count }: { count: number }) {
  return <ListingSkeleton count={count} className={SEARCH_GRID} />;
}

/**
 * Section of the page: the trail and the heading in a 16 px group, the blocks of the page 32 px
 * apart, 24 px (40 px from lg) above and 48 px below the content.
 */
export function SearchSection({
  breadcrumbs,
  breadcrumbsLabel,
  heading,
  children,
}: {
  breadcrumbs: BreadcrumbItem[];
  breadcrumbsLabel: string;
  /** The H1 – the empty search puts it into its card. */
  heading?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="custom-section pb-12 pt-6 lg:pt-10">
      <div className="custom-container flex flex-col gap-8">
        <div className="flex min-w-0 flex-col gap-4">
          <Breadcrumbs items={breadcrumbs} label={breadcrumbsLabel} />
          {heading}
        </div>
        {children}
      </div>
    </section>
  );
}

/** Server fallback while the client reads the query: the trail, a heading bar and the tile grid. */
export function SearchSkeleton({
  breadcrumbs,
  breadcrumbsLabel,
}: {
  breadcrumbs: BreadcrumbItem[];
  breadcrumbsLabel: string;
}) {
  return (
    <SearchSection
      breadcrumbs={breadcrumbs}
      breadcrumbsLabel={breadcrumbsLabel}
      heading={
        <span
          aria-hidden="true"
          className="block h-[34px] w-2/3 max-w-[420px] animate-pulse rounded-[8px] bg-[#F5F6F3] motion-reduce:animate-none md:h-[52px]"
        />
      }
    >
      <ProductsSkeleton count={8} />
    </SearchSection>
  );
}
